// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { captureException } from "@sentry/react";
import _ from "lodash";

import { CaseInsight } from "../../../../../api/APIClient";
import { GenderToDisplayName } from "../../../constants";
import { INDIVIDUALS_STRING, SENTENCE_TYPE_DISPLAY_NAMES } from "./constants";

export function getDescriptionGender(
  rollupGender: NonNullable<CaseInsight["rollupGender"]>,
) {
  let genderString;
  if (GenderToDisplayName[rollupGender] === GenderToDisplayName.MALE) {
    genderString = "men";
  } else if (GenderToDisplayName[rollupGender] === GenderToDisplayName.FEMALE) {
    genderString = "women";
  } else if (
    GenderToDisplayName[rollupGender] === GenderToDisplayName.NON_BINARY
  ) {
    genderString = "non-binary people";
  } else if (GenderToDisplayName[rollupGender] === GenderToDisplayName.TRANS) {
    genderString = "trans people";
  } else if (
    GenderToDisplayName[rollupGender] === GenderToDisplayName.TRANS_FEMALE
  ) {
    genderString = "trans women";
  } else if (
    GenderToDisplayName[rollupGender] === GenderToDisplayName.TRANS_MALE
  ) {
    genderString = "trans men";
  } else {
    genderString = INDIVIDUALS_STRING;
  }

  return ` ${genderString}`;
}

export function getSubtitleGender(gender: CaseInsight["rollupGender"]) {
  return gender
    ? `${_.chain(gender).lowerCase().startCase().value()}s`
    : undefined;
}

export function getSubtitleLsirScore(
  rollupAssessmentScoreBucketStart: number | null,
  rollupAssessmentScoreBucketEnd: number | null,
) {
  if (
    rollupAssessmentScoreBucketStart === null ||
    rollupAssessmentScoreBucketStart === -1 ||
    rollupAssessmentScoreBucketEnd === null
  ) {
    return undefined;
  }

  let subString;
  if (rollupAssessmentScoreBucketEnd === -1) {
    subString = `${rollupAssessmentScoreBucketStart}+`;
  } else {
    subString = `${rollupAssessmentScoreBucketStart}-${rollupAssessmentScoreBucketEnd}`;
  }

  return `LSI-R = ${subString}`;
}

export function getSentenceLengthBucketLabel(
  recommendationType: string | null,
  sentenceLengthBucketStart: number,
  sentenceLengthBucketEnd: number,
) {
  // If the bucket range is 0 to infinity, just return the recommendation type
  if (sentenceLengthBucketStart === 0 && sentenceLengthBucketEnd === -1) {
    if (!recommendationType) {
      captureException(
        new Error(
          "Recommendation type is null and there is no sentence length bucket!",
        ),
      );
    }

    const displayName = recommendationType
      ? SENTENCE_TYPE_DISPLAY_NAMES[recommendationType] ?? recommendationType
      : null;
    return displayName ?? "UNKNOWN";
  }

  // If the bucket is 0 - x, just make it < x year(s)
  if (sentenceLengthBucketStart === 0) {
    return `< ${sentenceLengthBucketEnd} Year${sentenceLengthBucketEnd > 1 ? "s" : ""} Incarceration`;
  }
  // If the bucket is x - infinity, just make it > x year(s)
  if (sentenceLengthBucketEnd === -1) {
    return `${sentenceLengthBucketStart}+ Years Incarceration`;
  }

  // Otherwise, return the range
  return `${sentenceLengthBucketStart}-${sentenceLengthBucketEnd} Years Incarceration`;
}

// Flat types (Probation, Treatment In Prison, Deferred Prosecution, Suspended) use {0, -1} as the
// convention for "no sentence length bucket".
export const CANONICAL_DISPOSITION_SLOTS = [
  {
    recommendationType: "Deferred Prosecution",
    sentenceLengthBucketStart: 0,
    sentenceLengthBucketEnd: -1,
  },
  {
    recommendationType: "Probation",
    sentenceLengthBucketStart: 0,
    sentenceLengthBucketEnd: -1,
  },
  {
    recommendationType: "Treatment In Prison",
    sentenceLengthBucketStart: 0,
    sentenceLengthBucketEnd: -1,
  },
  {
    recommendationType: "Suspended",
    sentenceLengthBucketStart: 0,
    sentenceLengthBucketEnd: -1,
  },
  {
    recommendationType: "Incarceration",
    sentenceLengthBucketStart: 1,
    sentenceLengthBucketEnd: 2,
  },
  {
    recommendationType: "Incarceration",
    sentenceLengthBucketStart: 3,
    sentenceLengthBucketEnd: 5,
  },
  {
    recommendationType: "Incarceration",
    sentenceLengthBucketStart: 6,
    sentenceLengthBucketEnd: -1,
  },
] satisfies Array<{
  recommendationType: string;
  sentenceLengthBucketStart: number;
  sentenceLengthBucketEnd: number;
}>;

export const LEGEND_LABELS: ReadonlyArray<string> =
  CANONICAL_DISPOSITION_SLOTS.map((s) =>
    getSentenceLengthBucketLabel(
      s.recommendationType,
      s.sentenceLengthBucketStart,
      s.sentenceLengthBucketEnd,
    ),
  );

export function getDispositionSlotKey(slot: {
  recommendationType: string | null;
  sentenceLengthBucketStart: number;
  sentenceLengthBucketEnd: number;
}) {
  return getSentenceLengthBucketLabel(
    slot.recommendationType,
    slot.sentenceLengthBucketStart,
    slot.sentenceLengthBucketEnd,
  );
}

/** Returns true for flat-type (non-incarceration) disposition buckets (Probation, Treatment). */
export function sentenceLengthLabelFilter(d: {
  sentenceLengthBucketStart: number;
  sentenceLengthBucketEnd: number;
}) {
  return d.sentenceLengthBucketStart === 0 && d.sentenceLengthBucketEnd === -1;
}

type SentenceLengthDataPoint = {
  recommendationType: string | null;
  sentenceLengthBucketStart: number;
  sentenceLengthBucketEnd: number;
};

export function sortDataForSentenceLengthCharts<
  T extends SentenceLengthDataPoint,
>(buckets: T[]) {
  // Return the buckets without sentence length ends and starts first,
  // then sort the rest by start value
  return [
    ...buckets.filter(sentenceLengthLabelFilter),
    ...buckets
      .filter((d) => !sentenceLengthLabelFilter(d))
      .sort(
        (a, b) => a.sentenceLengthBucketStart - b.sentenceLengthBucketStart,
      ),
  ];
}
